import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateLifeInsuranceTariff,
  useUpdateLifeInsuranceTariff,
} from "../../../../common/api/queries/lifeInsuranceTariff";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { LifeInsuranceTariffGroupSelect } from "../../../enumerations/components/form/LifeInsuranceTariffGroupSelect";
import { CreateUpdateLifeInsuranceTariff, LifeInsuranceTariff } from "../../types";

interface Props {
  open: boolean;
  tariff?: LifeInsuranceTariff;
  onFormCancel: () => void;
}

export const LifeInsuranceTariffForm = ({ open, tariff, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateLifeInsuranceTariff>();

  const createLifeInsuranceTariff = useCreateLifeInsuranceTariff();
  const updateLifeInsuranceTariff = useUpdateLifeInsuranceTariff();

  useEffect(() => {
    if (open && tariff) {
      form.setFieldsValue({ groupId: tariff.group.id, ...tariff });
    }
  }, [open, tariff, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (tariff) {
          updateLifeInsuranceTariff.mutate(
            { id: tariff.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "lifeInsuranceTariff.attrs", error.violations),
            },
          );
        } else {
          createLifeInsuranceTariff.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "lifeInsuranceTariff.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  const colSpan = 8;

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={t(tariff ? "lifeInsuranceTariff.titles.updateTariff" : "lifeInsuranceTariff.titles.createTariff")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createLifeInsuranceTariff.isPending || updateLifeInsuranceTariff.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={tariff} />

      <Form form={form} layout="vertical" name="lifeInsuranceTariffForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="groupId" label={t("lifeInsuranceTariff.attrs.groupId")} rules={[validations.notNull]}>
              <LifeInsuranceTariffGroupSelect disabled={!!tariff} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="code"
              label={t("lifeInsuranceTariff.attrs.code")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="primaryTariff"
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("lifeInsuranceTariff.attrs.primaryTariff")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t("lifeInsuranceTariff.attrs.name")}
              rules={[validations.notBlank, validations.size(1, 1024)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
